// Utility class for defining piecewise-defined functions,
// and evaluating them at arbitrary points.

// Both inputs and outputs can be arbitrary numbers, and the user can
// provide an interpolation function for each piecewise segment.

export class PieceFn<T = number> {
  private segments: [number, number, (t: number) => T][] = [];

  public addSegment(
    start: number,
    end: number,
    compute: (t: number) => T,
  ): this {
    this.segments.push([start, end, compute]);
    return this;
  }

  public evaluate(x: number): T {
    if (this.segments.length === 0) {
      throw new Error("PieceFn has no segments");
    }

    let segment = this.segments[0];

    // Find the segment that x is in
    for (let i = 1; i < this.segments.length; i++) {
      if (x >= this.segments[i][0]) {
        segment = this.segments[i];
      }
    }

    // clamp x to the segment
    x = Math.max(segment[0], Math.min(segment[1], x));

    // Evaluate the segment
    const [start, end, interpolate] = segment;
    const t = (x - start) / (end - start);
    return interpolate(t);
  }
}

// A cumulative piece function, where the output is the sum of all segments up to the given x.
export class CumulativePieceFn {
  private segments: [number, number, (t: number) => number][] = [];

  public addSegment(
    start: number,
    end: number,
    compute: (t: number) => number,
  ): CumulativePieceFn {
    this.segments.push([start, end, compute]);
    return this;
  }

  public evaluate(x: number): number {
    let result = 0;

    for (const segment of this.segments) {
      const [start, end, interpolate] = segment;

      if (x > end) {
        result += interpolate(1);
      } else if (x > start) {
        const t = (x - start) / (end - start);
        result += interpolate(t);
        break;
      }
    }

    return result;
  }
}

// A piecewise function defined using steps, like a step sequencer.
// 4:4 is assumed.
// Each step is a 16th note, and can be set to 0 or 1.
// The function is evaluated using fractional beats.
// The step length (how long each step lasts) is also in beats and can be configured.
// When a 1 is encountered, the function returns 1 immediately and then starts fading to 0, using the given fade function.
export class StepSequencer {
  constructor(private readonly steps: number[]) {
    // Each beat contains 4 steps
    // We want steps to be divisable by 4 to keep things simpler
    if (steps.length % 4 !== 0) {
      throw new Error("Steps must be divisible by 4");
    }
  }

  public evaluate(now: number): number {
    // Convert from beats to steps
    now *= 4;

    const stepIndex = Math.floor(now);
    const step = this.steps[stepIndex % this.steps.length];

    return step;
  }
}

/**
 * A version of StepSequencer that supports attack and release times.
 * Steps are binary, but the output is a float between 0 and 1.
 */
export class StatefulSmoothStepSequencer {
  private currentStepIndex: number | undefined = undefined;
  private currentStepStartTime: number | undefined = undefined;

  constructor(
    private readonly attackTime: number,
    private readonly releaseTime: number,
    private readonly steps: number[],
  ) {
    // Each beat contains 4 steps
    // We want steps to be divisable by 4 to keep things simpler
    if (steps.length % 4 !== 0) {
      throw new Error("Steps must be divisible by 4");
    }
  }

  public evaluate(now: number): number {
    // Convert from beats to steps
    now *= 4;

    // Warped, reset state
    if (this.currentStepStartTime && this.currentStepStartTime > now) {
      this.currentStepStartTime = undefined;
      this.currentStepIndex = undefined;
    }

    const stepIndex = Math.floor(now);
    const step = this.steps[stepIndex % this.steps.length];

    // If this is a new on step, reset the timer
    if (step === 1 && this.currentStepIndex !== stepIndex) {
      this.currentStepIndex = stepIndex;
      this.currentStepStartTime = now;
    }

    // Compute the value

    if (!this.currentStepStartTime) {
      return 0;
    }

    const attackEndTime = this.currentStepStartTime + this.attackTime;
    const releaseEndTime = attackEndTime + this.releaseTime;

    if (now < attackEndTime) {
      return (now - this.currentStepStartTime) / this.attackTime;
    } else if (now < releaseEndTime) {
      return 1 - (now - attackEndTime) / this.releaseTime;
    } else {
      return 0;
    }
  }
}
